/**
 * The loop guard's SOFT FAIL (MDOT-24553): when the agent loop is parked because it looks
 * runaway (turn cap reached, or the same tool call / the same reply repeated — see loopGuard.ts),
 * this notice appears above the composer and hands the decision to the user instead of either
 * spinning on or silently giving up.
 *
 * "Continue" grants the run a fresh budget and resumes it, with a hidden advisory telling the
 * model why it was interrupted so it doesn't replay the very step that tripped the guard.
 * "Stop" just clears the notice — the run stays where it is and the user can type instead.
 *
 * The state is provided by {@link SphereAIChatSection} (which owns the live chat) and consumed
 * here, inside the styled Thread's composer region; a React context is the bridge because the
 * Thread's composer slots receive no props (cf. AppContextChip, SphereAIComposerChoice).
 */
import { Button } from "@mdotm/shadcn/mdotm/components";
import { DialogFooter } from "@mdotm/shadcn/legacy/components";
import { createContext, useContext } from "react";

export type SphereAILoopGuardPrompt = {
	/** One-sentence, user-facing description of what the guard saw (from `LoopStop.summary`). */
	summary: string;
	/** Grant a fresh budget and resume the parked run. */
	onContinue: () => void;
	/** Leave the run parked and dismiss the notice. */
	onStop: () => void;
};

const SphereAILoopGuardContext = createContext<SphereAILoopGuardPrompt | null>(null);

export const SphereAILoopGuardProvider = SphereAILoopGuardContext.Provider;

/**
 * The notice itself — rendered above the composer, and nothing at all (`null`) whenever the
 * guard hasn't parked the run, which is the overwhelmingly common case.
 */
export function SphereAILoopNotice(): React.JSX.Element | null {
	const prompt = useContext(SphereAILoopGuardContext);

	if (!prompt) return null;

	// An `<output>` (implicitly `role="status"`) so a screen reader announces the pause.
	return (
		<output className="border-border bg-muted flex w-full flex-col gap-2 rounded-lg border border-dashed p-3">
			{/* Typography is inherited from the thread root (Body/L/Book), so no size class here. */}
			<span className="text-foreground">
				{prompt.summary} It has paused so you can decide whether it's worth carrying on.
			</span>
			{/* Same footer the platform's confirm dialogs use — this IS a confirm prompt, so it follows
			    the house convention rather than inventing one: the neutral action (Stop) on the left,
			    the primary (Continue) on the right, the two pushed to opposite edges so they read as
			    opposed choices instead of an adjacent pair. */}
			<DialogFooter
				neutralAction={
					<Button size="small" palette="tertiary" onClick={prompt.onStop}>
						Stop
					</Button>
				}
				primaryAction={
					<Button size="small" palette="primary" onClick={prompt.onContinue}>
						Continue
					</Button>
				}
			/>
		</output>
	);
}
